import { usePrimePredictionOrder } from '@src/components/PredictionMarkets/PredictionCard/usePrimePredictionOrder';
import { usePredictionsContext } from '@src/providers/PredictionsProvider';
import type { PredictionMarketsPriceGridPanelConfig } from '@src/types/LayoutConfig';
import {
  BlotterTable,
  BlotterTableFilters,
  LoaderTalos,
  useBlotterTable,
  usePersistedBlotterTable,
  VStack,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { usePriceGridMarketDataEnrichment } from '../../hooks';
import { useRFQAction } from '../../hooks/actions/rfq/useRFQAction';
import { PriceGridBody } from '../../PriceGridHeaderLayout';
import { getPredictionMarketsPriceGridColumns } from './columns';
import { getSelectedPredictionMarketSeriesIDs } from './filters';
import { getPredictionMarketsPriceGridOptions } from './gridOptions';
import { getPredictionMarketDataEnrichmentSpec } from './predictionMarketEnrichment';
import type { PredictionMarketOutcomeClickHandler } from './predictionMarketPriceColumn';
import { PredictionMarketsPriceGridHeader } from './PredictionMarketsPriceGridHeader';
import { getPredictionMarketsPriceView } from './priceView';
import type { PredictionMarketsPriceGridRow } from './rows';
import { usePredictionMarketsPriceGridFilter } from './usePredictionMarketsPriceGridFilter';
import { usePredictionMarketsPriceGridMarketSelection } from './usePredictionMarketsPriceGridMarketSelection';
import { usePredictionMarketsPriceGridRows } from './usePredictionMarketsPriceGridRows';

interface PredictionMarketsPriceGridProps {
  blotterID: string;
  parameters?: Pick<PredictionMarketsPriceGridPanelConfig, 'filterState'>;
  onParametersChange?: (parameters: Partial<Pick<PredictionMarketsPriceGridPanelConfig, 'filterState'>>) => void;
}

export function PredictionMarketsPriceGrid({
  blotterID,
  parameters,
  onParametersChange,
}: PredictionMarketsPriceGridProps) {
  const { eventsByID, eventSeries, isRefDataReady } = usePredictionsContext();
  const filtersPortalID = `${blotterID}-filters`;
  const { filterState, setFilterState, setSearch } = usePredictionMarketsPriceGridFilter({
    filterState: parameters?.filterState,
    onFilterStateChanged: nextFilterState => onParametersChange?.({ filterState: nextFilterState }),
  });
  const { seriesNames } = filterState;
  const selectedSeriesIDs = useMemo(
    () => getSelectedPredictionMarketSeriesIDs(eventSeries, seriesNames),
    [eventSeries, seriesNames]
  );
  const marketSelection = usePredictionMarketsPriceGridMarketSelection({
    filterState,
    onFilterStateChanged: setFilterState,
  });
  const gridOptions = useMemo(() => getPredictionMarketsPriceGridOptions(), []);
  const onRFQClick = useRFQAction();
  const primePredictionOrder = usePrimePredictionOrder();
  const priceView = getPredictionMarketsPriceView(filterState);
  const handleOutcomeClick = useCallback<PredictionMarketOutcomeClickHandler>(
    ({ row, outcome, price }) => {
      primePredictionOrder({
        symbol: row.security.Symbol,
        outcome,
        price,
        orderQty: row.bucket.size,
        marketAccounts: marketSelection.selectedMarketAccount
          ? [marketSelection.selectedMarketAccount.Name]
          : undefined,
      });
    },
    [marketSelection.selectedMarketAccount, primePredictionOrder]
  );
  const columns = useMemo(
    () =>
      getPredictionMarketsPriceGridColumns({
        selectedMarketAccount: marketSelection.selectedMarketAccount,
        priceView,
        onOutcomeClick: handleOutcomeClick,
        onRFQClick,
      }),
    [handleOutcomeClick, marketSelection.selectedMarketAccount, onRFQClick, priceView]
  );
  const dataObservable = usePredictionMarketsPriceGridRows({
    eventsByID,
    filterState,
    market: marketSelection.market,
    selectedSeriesIDs,
  });
  const marketDataSpec = useMemo(
    () => getPredictionMarketDataEnrichmentSpec(marketSelection.selectedMarketAccount?.Name),
    [marketSelection.selectedMarketAccount?.Name]
  );
  const marketDataEnrichment = usePriceGridMarketDataEnrichment({ dataObservable, marketDataSpec });
  const resolvedGridOptions = useMemo(
    () => ({
      ...gridOptions,
      ...marketDataEnrichment.gridOptionsOverlay,
    }),
    [gridOptions, marketDataEnrichment.gridOptionsOverlay]
  );
  const persistence = usePersistedBlotterTable(blotterID);
  const blotterTable = useBlotterTable<PredictionMarketsPriceGridRow>({
    rowID: 'rowID',
    dataObservable: marketDataEnrichment.enrichedDataObservable,
    errorObservable: undefined,
    columns,
    persistence,
    gridOptions: resolvedGridOptions,
  });

  return (
    <VStack h="100%" w="100%" alignItems="flex-start" data-testid="prediction-markets-price-grid">
      {isRefDataReady ? (
        <>
          <PredictionMarketsPriceGridHeader
            filtersPortalID={filtersPortalID}
            filterState={filterState}
            onFilterStateChanged={setFilterState}
            marketSelection={marketSelection}
          />
          <PriceGridBody>
            <BlotterTableFilters
              {...blotterTable.blotterTableFiltersProps}
              portalId={filtersPortalID}
              quickFilterText={filterState.search ?? ''}
              onQuickFilterTextChanged={setSearch}
              showFilterBuilder={false}
              showDateRangePicker={false}
            />
            <BlotterTable {...blotterTable} />
          </PriceGridBody>
        </>
      ) : (
        <PriceGridBody>
          <VStack h="100%" w="100%" justifyContent="center" alignItems="center">
            <LoaderTalos delay={150} />
          </VStack>
        </PriceGridBody>
      )}
    </VStack>
  );
}
